import { useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getSubmissionCoverage,
  upsertCoverageOption,
} from '../../../../../api/submissions/submission-coverages.api';
import type {
  CoverageOption,
  CoverageOptionDto,
  SubmissionCoverageDto,
} from '../../../../../api/submissions/submission-coverages.types';
import { getData } from '../../../../../utils/functional/fp';
import { invalidateSubmissionCoverage } from './invalidateSubmissionCoverage';
import { hasQuote, isDuplicateTerms } from './quoteOptionsModel';
import { submissionCoverageKey, useQuoteOptions } from './useQuoteOptions';
import { addToQuoteCartVisit, isInQuoteCartVisit } from './quoteCartVisit';

export type SaveQuoteOptionArgs = {
  readonly product: CoverageOptionDto['product'];
  readonly terms: unknown;
  readonly indication: CoverageOptionDto['indication'];
  readonly parts: CoverageOptionDto['coverages'];
  readonly tower?: CoverageOptionDto['tower'];
};

type Params = {
  readonly submissionId: string;
  readonly seedOptionId?: string;
};

/**
 * Returns a save function. A save from a page opened with ?optionId updates that option while it
 * is still unquoted and no other save from the page has changed it, terms or not: the option was
 * opened to be edited, so an option already in the cart is saved in place rather than copied.
 * Every later save creates a new option, as does a save after a sibling on the page (another
 * preset's Add) filled the option first. A seed lookup that fails rejects the save, so the seed
 * stays unfilled and the next attempt looks it up again.
 */
export const useSaveQuoteOption = ({ submissionId, seedOptionId }: Params) => {
  const queryClient = useQueryClient();
  const seedConsumed = useRef(false);
  const optionsQuery = useQuoteOptions(seedOptionId ? submissionId : undefined);
  // The seed's terms as the page opened it, read once before any save from the page can change them.
  const openedSeed = useRef<{ readonly terms: unknown } | null>(null);
  const seedOnOpen = optionsQuery.data?.coverageOptions.find(
    (option) => option.id === seedOptionId
  );
  if (openedSeed.current === null && seedOnOpen) {
    openedSeed.current = { terms: seedOnOpen.terms ?? null };
  }

  // Resolves against fresh data. A seed that is missing or quoted means "create new". A failed
  // lookup rejects: creating a new option then would leave the seed unfilled.
  const canUpdateSeed = async (): Promise<boolean> => {
    // The visit outlives this hook: a seed an earlier page of the visit (simple, then customize) already
    // saved into is in the cart, and the next Add creates a new option rather than overwriting it.
    if (
      !seedOptionId ||
      seedConsumed.current ||
      isInQuoteCartVisit(submissionId, seedOptionId)
    ) {
      return false;
    }
    const queryKey = submissionCoverageKey(submissionId);
    const queryFn = () => getSubmissionCoverage(submissionId).then(getData);
    // A save before the page has rendered the seed waits for the read the page started on mount,
    // which predates any save from the page, rather than treating a seed with terms as changed.
    if (openedSeed.current === null) {
      const opened =
        queryClient.getQueryData<SubmissionCoverageDto>(queryKey) ??
        (await queryClient.fetchQuery<SubmissionCoverageDto>({
          queryKey,
          queryFn,
        }));
      const seedOnLoad = opened.coverageOptions.find(
        (option) => option.id === seedOptionId
      );
      if (openedSeed.current === null && seedOnLoad) {
        openedSeed.current = { terms: seedOnLoad.terms ?? null };
      }
    }
    const coverage = await queryClient.fetchQuery<SubmissionCoverageDto>({
      queryKey,
      queryFn,
      staleTime: 0,
    });
    const seed = coverage.coverageOptions.find(
      (option) => option.id === seedOptionId
    );
    if (!seed || hasQuote(seed) || !openedSeed.current) {
      return false;
    }
    return isDuplicateTerms(seed.terms ?? null, openedSeed.current.terms);
  };

  return async (args: SaveQuoteOptionArgs): Promise<CoverageOption> => {
    const updateSeed = await canUpdateSeed();

    const saved = await upsertCoverageOption(submissionId, {
      data: {
        ...(updateSeed && { id: seedOptionId }),
        product: args.product,
        coverages: args.parts,
        terms: args.terms,
        indication: args.indication,
        ...(args.tower && { tower: args.tower }),
      },
    }).then(getData);

    seedConsumed.current = true;
    addToQuoteCartVisit(submissionId, saved.id, updateSeed);
    await invalidateSubmissionCoverage(queryClient, submissionId);
    return saved;
  };
};
